import {
  FEAASComponentProps,
  FEAASElement,
  fetchComponent,
  getComponentURL,
  getStylesheetURL,
  parseComponentSource,
  FEAASCDNStylesheetSource,
  FEAASCDNStylesheetParams,
  parseStylesheetSource,
  getStylesheetSource
} from './index.js'
import React from 'react'
import { renderHTMLContent, HTMLNode, parseHTMLContent } from '../dom/html.js'
import { DataSettings } from '../utils/settings.js'
import { External, FEAASExternalPropsObject, getComponent, objectKeysToKebabCase } from './FEAASExternal.js'
import { reverseKebabCase } from '../utils/string.js'
export { renderHTMLContent }
export { External, linkComponents, registerComponent, getComponent, FEAASExternalPropsObject } from './FEAASExternal.js'
export * from './index.js'
export * from '../headless.js'

export type FEAASReactExternalComponentProps = { componentName: string; fallback?: any; [prop: string]: any }

export type FEAASReactComponentProps = FEAASComponentProps & {
  preload?: boolean
  lastModified?: Date | string
}

/**
 * A component to include a stylesheet using a <link> tag. Placing one in the HEAD ensures that there will be no flash
 * of unstyled content.
 *
 * @param {FEAASCDNStylesheetParams | FEAASCDNStylesheetSource} props  - The properties for including the stylesheet.
 * @returns {JSX.Element} The <link> element for the stylesheet.
 */
export const FEAASReactStylesheet = (props: FEAASCDNStylesheetParams | FEAASCDNStylesheetSource) => {
  if ('src' in props) {
    props = parseStylesheetSource(props.src)
  }
  return <link rel='stylesheet' href={getStylesheetSource(props)} />
}

/**
 * An asynchronous wrapper over FEAAS.Component for use in React, especially suited for Next.js.
 * This component fetches HTML and data asynchronously for server-side rendering with data mapping.
 *
 * @param {FEAASReactComponentProps} props  - The properties for the component.
 * @returns {Promise<JSX.Element>} The JSX element representing the component.
 */
export const FEAASReactServerComponent = async ({ ...props }: FEAASReactComponentProps) => {
  const fetchedData = await DataSettings.fetch(props?.data || {})

  const p: FEAASReactComponentProps = { ...props }

  if (('src' in props || 'component' in props) && props?.template == null) {
    const response = await fetchComponent(props)

    p.template = response.template
    p.lastModified = response.lastModified
    //@ts-ignore
    p.hydrate = false
  }

  return FEAASReactComponent({ ...p, data: fetchedData })
}

/**
 * A wrapper over FEAAS.Component for use in React. It converts a passed template HTML into React,
 * allowing external components in the render tree. Data can be provided in JSON format.
 *
 * @param {FEAASReactComponentProps} props  - The properties for the component.
 * @returns {JSX.Element} The JSX element representing the component.
 */
export function FEAASReactComponent({ data, ...props }: FEAASReactComponentProps) {
  let preload = null
  let children: null | React.ReactNode

  if ('template' in props && props.template) {
    children = parseHTMLContent(props.template, data || {}).map((node, index) =>
      HTMLNodeToReact(
        node,
        (node, attributes) => {
          if (node.localName == 'feaas-external') {
            const camelized = {} as any
            for (var prop in attributes) {
              const key = prop == 'data-external-id' ? 'componentName' : reverseKebabCase(prop)
              camelized[key] = attributes[prop]
            }
            return [FEAASExternalComponent, camelized]
          }
        },
        index
      )
    )
  }

  if ('src' in props) {
    props = { ...parseComponentSource(props.src), ...props }
  }

  return (
    <>
      {'preload' in props && preload === true && 'src' in props && (
        <>
          <link rel='preload' as='fetch' href={getComponentURL(props.src)} crossOrigin='anonymous' />
          <link rel='preload' as='style' href={getStylesheetURL(props.src)} crossOrigin='anonymous' />
        </>
      )}
      <feaas-component
        class='-feaas'
        {...props}
        suppressHydrationWarning
        template={null}
        data={typeof data == 'string' ? data : data ? JSON.stringify(data) : null}
        hydrate={'false'}
      >
        {children}
      </feaas-component>
    </>
  )
}

/**
 * A wrapper over External component that accepts a fallback for rendering in case of a missing component.
 * It accepts properties in camelcase format and ensures no hydration errors on the client-side.
 *
 * @param {FEAASReactExternalComponentProps} props  - The properties for the component.
 * @returns {JSX.Element} The JSX element representing the component.
 */
export function FEAASReactExternalComponent(props: FEAASReactExternalComponentProps) {
  const { componentName, fallback, ...attributes } = props
  const Component = getComponent(componentName)?.component
  if ((!Component && fallback) || !componentName)
    return (
      <feaas-external data-external-id={componentName} suppressHydrationWarning hydrate={'false'}>
        {fallback}
      </feaas-external>
    )
  const parsed = FEAASExternalPropsObject(componentName, attributes)
  delete parsed['suppressHydrationWarning']
  // execute component as a function to supress hydration errors inside
  return (
    <>
      <feaas-external
        {...objectKeysToKebabCase(parsed)}
        data-external-id={componentName}
        suppressHydrationWarning
        hydrate={'false'}
      >
        {Component == null ? null : <Component {...parsed} />}
      </feaas-external>
    </>
  )
}

/**
 * Converts an HTMLNode into a React tree, ensuring proper context access for external components.
 *
 * @param {HTMLNode} node        - The HTML node to be converted.
 * @param {any}      [callback]  - Callback to customize turning of HTML node into react tree.
 * @param {any}      [key]       - The key for the React element.
 * @returns {React.ReactNode} The resulting React node.
 */
export function HTMLNodeToReact(
  node: HTMLNode,
  callback?: (node: HTMLNode, attributes: Record<string, any>) => [tagName: any, attributes: Record<string, any>],
  key?: any
): React.ReactNode {
  if (node.nodeType === 3) {
    return node.textContent
  }
  const children = Array.from(node.childNodes).map((child, index) => HTMLNodeToReact(child, callback, index))

  const attributes: { [key: string]: string | Record<string, string> } = {}
  for (let i = 0; i < node.attributes.length; i++) {
    const { name, value } = node.attributes[i]
    if (name == 'style') {
      attributes[name] = String(value || '')
        .trim()
        .split(/\s*;\s*/)
        .reduce((object, pair) => {
          const [k, v] = pair.split(':')
          return {
            ...object,
            [k]: v
          }
        }, {})
    } else if (name == 'class') {
      attributes.className = value
    } else {
      attributes[name] = value
    }
  }

  const reactNode = callback?.(node, attributes)
  const reactName = reactNode?.[0] || node.nodeName.toLowerCase()
  const reactAttributes = Object.assign({ key, suppressHydrationWarning: true }, reactNode?.[1] || attributes)
  return React.createElement(reactName, reactAttributes, ['img', 'br', 'input'].includes(reactName) ? null : children)
}

const NextComponents = new Map()
var NextClientsideRenderer: any = null
var NextDynamicFunction: any = null

/**
 * A special case of ExternalComponent designed for Next.js.
 * It uses `next/dynamic` to control rendering on the client, server, or both.
 *
 * @param {FEAASReactExternalComponentProps} props  - The properties for the component.
 * @returns {JSX.Element} The JSX element representing the component.
 */
export function FEAASNextExternalComponent(props: FEAASReactExternalComponentProps) {
  // See if component is registered in current context
  const { componentName, ...attributes } = props
  const Component = getComponent(componentName)?.component
  if (!NextComponents.get(componentName)) {
    // Designate component for rendering on clientside
    const Dynamic = NextDynamicFunction(() => Promise.resolve(NextClientsideRenderer), {
      ssr: false,
      loading: () => <FEAASReactExternalComponent {...attributes} componentName={componentName} />
    })

    NextComponents.set(componentName, Dynamic)
  }

  const External = NextComponents.get(componentName)
  return <External {...props} fallback={Component ? <Component {...props} /> : null}></External>
}

/**
 * Enables Next.js client/server rendering options for registered components.
 * The `component` argument should be a function exported from a file with a `use client` directive.
 *
 * @param {any} dynamic    - The Next dynamic function.
 * @param {any} component  - The component function.
 * @returns {any} Augmented bundle.
 */
export function enableNextClientsideComponents(dynamic: any, component: any) {
  NextDynamicFunction = dynamic
  NextClientsideRenderer = component
  return component
}

/**
 * Renders a registered external component in a React tree.
 *
 * @param {FEAASReactExternalComponentProps} props  - The properties for the component.
 * @returns {JSX.Element} The JSX element representing the component.
 */
export function FEAASExternalComponent(props: FEAASReactExternalComponentProps) {
  if (Object.keys(props).length == 0) {
    return
  }
  if (NextDynamicFunction) return FEAASNextExternalComponent(props)
  return FEAASReactExternalComponent(props)
}
export function FEAASReactExternalComponentBundle() {
  return (
    <>
      <feaas-registration components={JSON.stringify(Object.values(External.registered))} suppressHydrationWarning />
      {/** Will not be rendered, but needs to be referenced */}
      {<NextClientsideRenderer />}
    </>
  )
}

export const ExternalComponentBundle = FEAASReactExternalComponentBundle
export const ExternalComponent = FEAASExternalComponent
export const ReactExternalComponent = FEAASReactExternalComponent
export const NextExternalComponent = FEAASNextExternalComponent
export const Component = FEAASReactComponent
export const ServerComponent = FEAASReactServerComponent
export const Stylesheet = FEAASReactStylesheet
